import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const SkillsInAction = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const projectSkills = [
    {
      id: 1,
      name: "E-Commerce Platform",
      description: "Full-stack marketplace with real-time features",
      image: "/assets/Projects/E-Commerce Platform.png",
      technologies: [
        { name: "React", usage: "Frontend UI components and state management", color: "bg-blue-500" },
        { name: "Node.js", usage: "RESTful API development and server logic", color: "bg-green-500" },
        { name: "MongoDB", usage: "Product catalog and user data storage", color: "bg-green-600" },
        { name: "Redis", usage: "Session management and caching layer", color: "bg-red-500" },
        { name: "AWS", usage: "Cloud hosting and file storage", color: "bg-orange-500" }
      ],
      metrics: {
        performance: "40% faster load times",
        users: "10,000+ active users",
        uptime: "99.9% availability"
      }
    },
    {
      id: 2,
      name: "Real-time Chat Application",
      description: "Scalable messaging platform with video calls",
      image: "/assets/Projects/Real-time Chat Application.png",
      technologies: [
        { name: "React", usage: "Real-time UI updates and message rendering", color: "bg-blue-500" },
        { name: "Socket.io", usage: "WebSocket connections and real-time events", color: "bg-purple-500" },
        { name: "Express", usage: "API endpoints and middleware handling", color: "bg-gray-600" },
        { name: "PostgreSQL", usage: "Message history and user relationships", color: "bg-blue-600" },
        { name: "Docker", usage: "Containerization and deployment", color: "bg-blue-400" }
      ],
      metrics: {
        messages: "1M+ messages daily",
        latency: "<50ms response time",
        concurrent: "5,000+ concurrent users"
      }
    },
    {
      id: 3,
      name: "Analytics Dashboard",
      description: "Data visualization platform for business insights",
      image: "/assets/Projects/Analytics Dashboard.png",
      technologies: [
        { name: "React", usage: "Interactive charts and dashboard components", color: "bg-blue-500" },
        { name: "D3.js", usage: "Custom data visualizations and animations", color: "bg-orange-600" },
        { name: "Python", usage: "Data processing and machine learning models", color: "bg-yellow-500" },
        { name: "FastAPI", usage: "High-performance API for data endpoints", color: "bg-green-400" },
        { name: "Kubernetes", usage: "Orchestration and auto-scaling", color: "bg-blue-700" }
      ],
      metrics: {
        dataPoints: "100M+ data points processed",
        insights: "500+ automated insights",
        accuracy: "95% prediction accuracy"
      }
    }
  ];

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-text-primary mb-4">Skills in Action</h2>
        <p className="text-lg text-text-secondary max-w-3xl mx-auto">
          See how my technical expertise translates into real-world solutions. Each project demonstrates 
          practical application of technologies and their impact on business outcomes.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {projectSkills.map((project) => (
          <div
            key={project.id}
            className="bg-card rounded-xl border border-border shadow-premium hover:shadow-lg transition-premium hover-lift cursor-pointer"
            onClick={() => setSelectedProject(project)}
          >
            <div className="relative overflow-hidden rounded-t-xl">
              <Image
                src={project.image}
                alt={project.name}
                className="w-full h-48 object-cover transition-transform duration-300 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-xl font-bold text-white mb-1">{project.name}</h3>
                <p className="text-sm text-white/90">{project.description}</p>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-text-primary mb-3">Technologies Used:</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.slice(0, 3).map((tech, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1 rounded-full text-white text-sm font-medium ${tech.color}`}
                      >
                        {tech.name}
                      </span>
                    ))}
                    {project.technologies.length > 3 && (
                      <span className="px-3 py-1 rounded-full bg-surface text-text-secondary text-sm font-medium border border-border">
                        +{project.technologies.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-text-primary mb-3">Key Metrics:</h4>
                  <div className="space-y-2">
                    {Object.entries(project.metrics).map(([key, value], idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Icon name="TrendingUp" size={16} className="text-success" />
                        <span className="text-sm text-text-secondary">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button className="w-full mt-4 px-4 py-2 bg-primary/10 text-primary rounded-lg border border-primary/20 hover:bg-primary/20 transition-premium flex items-center justify-center space-x-2">
                  <Icon name="ExternalLink" size={16} />
                  <span>View Details</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal for detailed view */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-border p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-text-primary">{selectedProject.name}</h2>
              <button
                onClick={() => setSelectedProject(null)}
                className="p-2 hover:bg-surface-hover rounded-lg transition-premium"
              >
                <Icon name="X" size={24} className="text-text-secondary" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <Image
                    src={selectedProject.image}
                    alt={selectedProject.name}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                  <p className="text-text-secondary mt-4">{selectedProject.description}</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-text-primary mb-4">Technology Implementation</h3>
                    <div className="space-y-4">
                      {selectedProject.technologies.map((tech, idx) => (
                        <div key={idx} className="p-4 bg-surface rounded-lg border border-border">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`w-3 h-3 rounded-full ${tech.color}`} />
                            <h4 className="font-semibold text-text-primary">{tech.name}</h4>
                          </div>
                          <p className="text-sm text-text-secondary">{tech.usage}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-text-primary mb-4">Project Impact</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {Object.entries(selectedProject.metrics).map(([key, value], idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-success/10 rounded-lg border border-success/20">
                          <span className="font-medium text-text-primary capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                          <span className="text-success font-semibold">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsInAction;